import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, Clock, Trophy, X } from 'lucide-react';
import { api, parseApiError, type ClanMember } from '../lib/api';
import { captainLabel, autoSelectSoleComp } from '../lib/roles';
import { cn } from '../lib/cn';

type Toast = { kind: 'removed' | 'role' | 'error'; msg: string; undo?: () => void } | null;

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

// Exported — CaptainsPanel/ClanTeamsBoard's "Extend" affordance on an
// existing WINDOWED grant reuses these SAME three presets (2026-07-21) so
// "time-boxed" means the same three durations everywhere a leader picks
// one, whether minting a new stand-in or extending one that's about to lapse.
export const DURATION_OPTIONS = [
  { label: '24h', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '7 days', hours: 168 },
] as const;

export function MakeCaptainModal({
  member,
  clanName,
  onClose,
  onToast,
  presetTeamNo,
}: {
  member: ClanMember;
  clanName: string;
  onClose: () => void;
  onToast: (t: Toast) => void;
  /**
   * Pre-select a team in the team picker — used when opening from a team
   * card. When absent (opened from a general member row), the picker starts
   * empty and the caller must choose one — Team Captain is per-team, always
   * (Lisa, 2026-07-21 — the "Clan-wide" option is retired; see MakeCaptainModal's
   * team <select> below and the mint endpoint's 400 on null/absent teamNo).
   */
  presetTeamNo?: number;
}) {
  // All hooks at the top — above any conditional return — per React hooks rules.
  const qc = useQueryClient();
  const [teamNo, setTeamNo] = useState<number | null>(presetTeamNo ?? null);
  const [teamName, setTeamName] = useState('');
  // Appoint choice (GOD-policy L6; COMP_SCOPED added 2026-07-21): PERMANENT
  // (the minted Team Captain) is the default — a standing appointment, no
  // expiry. Time-boxed stand-in is the pre-existing windowed grant, opt-in.
  // COMP_SCOPED lasts for the duration of ONE competition — ends
  // automatically when that comp ends (shared/compScopedGrantSweep.ts on the
  // backend), revocable early exactly like the other two kinds.
  const [appointKind, setAppointKind] = useState<'PERMANENT' | 'WINDOWED' | 'COMP_SCOPED'>('PERMANENT');
  const [durationHours, setDurationHours] = useState(168);
  const [selectedCompId, setSelectedCompId] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // Comp picker source (2026-07-21): reuses the SAME query ClanHome.tsx
  // already runs (queryKey ['publicClanPage', clanName]) — react-query
  // dedupes/caches across both, so opening this modal after visiting the
  // clan's public page is a cache hit, and opening it cold just fetches once.
  // `.comps` is already filtered server-side to LIVE/PENDING (never CLOSED)
  // — exactly "the clan's ACTIVE enrollments" this picker needs.
  const compsQuery = useQuery({
    queryKey: ['publicClanPage', clanName],
    queryFn: () => api.publicClanPage(clanName),
  });
  const enrolledComps = compsQuery.data?.comps ?? [];

  // Auto-select the sole comp when there's exactly one active enrollment —
  // no point making the leader pick from a list of one. Re-runs if the
  // comps list resolves/changes after mount (query was still loading).
  useEffect(() => {
    setSelectedCompId(prev => prev ?? autoSelectSoleComp(enrolledComps));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compsQuery.data]);

  const mintMut = useMutation({
    mutationFn: (supersede: boolean) =>
      api.mintDelegation(clanName, {
        granteeDiscordId: member.discordId,
        teamNo: teamNo ?? undefined,
        teamName: teamName.trim() || undefined,
        kind: appointKind,
        durationHours: appointKind === 'WINDOWED' ? durationHours : undefined,
        compId: appointKind === 'COMP_SCOPED' ? selectedCompId ?? undefined : undefined,
        supersede,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
      const label = captainLabel(teamNo, teamName.trim() || undefined);
      const roleLabel =
        appointKind === 'PERMANENT' ? 'Team Captain'
        : appointKind === 'COMP_SCOPED' ? 'Comp Captain'
        : 'Stand-in captain';
      onToast({ kind: 'role', msg: `${member.gamertag ?? member.displayName ?? shortId(member.discordId)} is now ${roleLabel} · ${label}.` });
      onClose();
    },
    onError: (e: Error) => {
      const msg = e.message;
      if (msg.includes('409') || msg.toLowerCase().includes('already has a captain')) {
        setConflict(true);
      } else if (msg.includes('429') || msg.toLowerCase().includes('cap')) {
        onToast({ kind: 'error', msg: 'Captain cap reached (10). Revoke one before adding another.' });
        onClose();
      } else {
        onToast({ kind: 'error', msg: parseApiError(e).message });
        onClose();
      }
    },
  });

  return (
    <>
      <div
        className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-forge-500/40 bg-iron-950 p-5 shadow-forged sm:inset-x-auto sm:bottom-auto sm:right-6 sm:top-1/2 sm:w-full sm:max-w-sm sm:-translate-y-1/2 sm:border">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center border border-forge-500/40 bg-forge-500/10">
            <Star className="h-4 w-4 text-forge-300" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm lg:text-base font-black uppercase tracking-wide text-white">
              Make {member.gamertag ?? member.displayName ?? shortId(member.discordId)} a Captain
            </p>
            <p className="mt-1 text-xs lg:text-sm text-iron-400">
              {appointKind === 'PERMANENT'
                ? 'Appoint a standing Team Captain for this team — no expiry.'
                : appointKind === 'COMP_SCOPED'
                ? 'Appoint a captain for the current competition — ends automatically when it does.'
                : 'Grant time-boxed stand-in captain access for this member.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-7 w-7 shrink-0 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-iron-600 hover:text-iron-300"
            aria-label="Close"
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </div>

        {/* Form fields */}
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-400">
              Team
            </label>
            <select
              value={teamNo ?? ''}
              onChange={e => {
                setTeamNo(e.target.value ? Number(e.target.value) : null);
                setConflict(false);
              }}
              className="w-full border border-iron-700 bg-iron-950 px-3 py-2 text-xs lg:text-sm text-iron-100 focus:border-forge-500 focus:outline-none"
            >
              {/* Captaincy is per-team (Lisa, 2026-07-21) — no "Clan-wide"
                  option. That role is Co-Admin now. This placeholder can't be
                  submitted; a real team must be chosen. */}
              <option value="" disabled>Select a team…</option>
              {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>Team {n}</option>
              ))}
            </select>
          </div>

          {teamNo !== null && (
            <div>
              <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-400">
                Team name (optional)
              </label>
              <input
                type="text"
                value={teamName}
                onChange={e => setTeamName(e.target.value)}
                placeholder="e.g. Alpha Squad"
                maxLength={40}
                className="w-full border border-iron-700 bg-iron-950 px-3 py-2 text-xs lg:text-sm text-iron-100 placeholder-iron-600 focus:border-forge-500 focus:outline-none"
              />
            </div>
          )}

          <div>
            <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-400">
              Appointment
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAppointKind('PERMANENT')}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 border px-2 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors',
                  appointKind === 'PERMANENT'
                    ? 'border-forge-500/60 bg-forge-950/60 text-forge-200'
                    : 'border-iron-700 bg-iron-950 text-iron-400 hover:border-iron-600 hover:text-iron-300'
                )}
              >
                <Star className="h-2.5 w-2.5" strokeWidth={2.5} />
                Team Captain
              </button>
              <button
                type="button"
                onClick={() => !compsQuery.isLoading && enrolledComps.length > 0 && setAppointKind('COMP_SCOPED')}
                disabled={!compsQuery.isLoading && enrolledComps.length === 0}
                title={!compsQuery.isLoading && enrolledComps.length === 0 ? 'This clan has no active competition to scope a captain to' : undefined}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 border px-2 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  appointKind === 'COMP_SCOPED'
                    ? 'border-forge-500/60 bg-forge-950/60 text-forge-200'
                    : 'border-iron-700 bg-iron-950 text-iron-400 hover:border-iron-600 hover:text-iron-300'
                )}
              >
                <Trophy className="h-2.5 w-2.5" strokeWidth={2.5} />
                For this comp
              </button>
              <button
                type="button"
                onClick={() => setAppointKind('WINDOWED')}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 border px-2 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors',
                  appointKind === 'WINDOWED'
                    ? 'border-forge-500/60 bg-forge-950/60 text-forge-200'
                    : 'border-iron-700 bg-iron-950 text-iron-400 hover:border-iron-600 hover:text-iron-300'
                )}
              >
                <Clock className="h-2.5 w-2.5" strokeWidth={2.5} />
                Time-boxed
              </button>
            </div>
            {appointKind !== 'COMP_SCOPED' && !compsQuery.isLoading && enrolledComps.length === 0 && (
              <p className="mt-1.5 text-[10px] text-iron-500">
                "For this comp" is unavailable — this clan isn't currently enrolled in a competition.
              </p>
            )}
          </div>

          {appointKind === 'COMP_SCOPED' && (
            <div>
              <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-400">
                Competition
              </label>
              {enrolledComps.length > 1 ? (
                <select
                  value={selectedCompId ?? ''}
                  onChange={e => setSelectedCompId(e.target.value || null)}
                  className="w-full border border-iron-700 bg-iron-950 px-3 py-2 text-xs lg:text-sm text-iron-100 focus:border-forge-500 focus:outline-none"
                >
                  <option value="" disabled>Select a competition…</option>
                  {enrolledComps.map(c => (
                    <option key={c.compId} value={c.compId}>{c.name}</option>
                  ))}
                </select>
              ) : (
                <p className="border border-iron-800 bg-iron-950/60 px-3 py-2 text-xs lg:text-sm text-iron-300">
                  {enrolledComps[0]?.name ?? (compsQuery.isLoading ? 'Loading…' : '—')}
                </p>
              )}
            </div>
          )}

          {appointKind === 'WINDOWED' && (
            <div>
              <label className="mb-1 block font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-400">
                Duration
              </label>
              <div className="flex gap-2">
                {DURATION_OPTIONS.map(opt => (
                  <button
                    key={opt.hours}
                    type="button"
                    onClick={() => setDurationHours(opt.hours)}
                    className={cn(
                      'flex-1 border px-2 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors',
                      durationHours === opt.hours
                        ? 'border-forge-500/60 bg-forge-950/60 text-forge-200'
                        : 'border-iron-700 bg-iron-950 text-iron-400 hover:border-iron-600 hover:text-iron-300'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 409 conflict inline prompt */}
        {conflict && (
          <div className="mt-3 border border-amber-500/30 bg-amber-950/30 px-3 py-2.5">
            <p className="text-xs lg:text-sm text-amber-300">
              Team {teamNo} already has a captain. Replace them?
            </p>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => setConflict(false)}
                className="flex-1 border border-iron-700 bg-iron-950 px-2 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-iron-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setConflict(false); mintMut.mutate(true); }}
                disabled={mintMut.isPending}
                className="flex-1 border border-amber-500/60 bg-amber-950/60 px-2 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-amber-100 transition-colors hover:border-amber-400 disabled:opacity-50"
              >
                Replace
              </button>
            </div>
          </div>
        )}

        {/* Primary actions */}
        {!conflict && (
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-iron-700 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-iron-200 transition-colors hover:border-iron-500 hover:bg-iron-900 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => mintMut.mutate(false)}
              disabled={mintMut.isPending || teamNo === null || (appointKind === 'COMP_SCOPED' && !selectedCompId)}
              title={
                teamNo === null ? 'Pick a team first — captaincy is per-team'
                : (appointKind === 'COMP_SCOPED' && !selectedCompId) ? 'Pick a competition first'
                : undefined
              }
              className="flex-1 border border-forge-500/60 bg-forge-950/60 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-forge-100 transition-all hover:border-forge-400 hover:bg-forge-900/70 hover:text-white disabled:opacity-50"
            >
              <Star className="mr-1 inline h-3 w-3" strokeWidth={2.5} />
              {mintMut.isPending
                ? 'Granting…'
                : teamNo === null
                ? 'Select a team'
                : appointKind === 'PERMANENT' ? 'Appoint Team Captain'
                : appointKind === 'COMP_SCOPED' ? (selectedCompId ? 'Appoint Comp Captain' : 'Select a competition')
                : 'Grant Stand-in'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
